<template>
  <div class="entry" @click="navigateToDetails" role="button" tabindex="0" @keyup.enter="navigateToDetails">
    <div class="info">
      <div class="info__top">
        <app-icon class="entry__icon" :src="entry.object.icon_uri" size="57px" />
        <div class="details">
          <p class="heading">
            {{ entry.object.name }}<sup class="jit-badge" v-if="entry.supports_jit === 1">JIT</sup><template
              v-if="entry.object.version">, v{{ entry.object.version }}</template>
          </p>

          <p class="description" v-if="entry.alongside_id">
            {{ $t('INSTALLED_TO_DEVICE_ALONGSIDE', {
              installedAt: new Date(entry.queued_at * 1000).toLocaleString(),
              alongsideID: entry.alongside_id,
              deviceModel: entry.device_model
            }) }}
          </p>
          <p class="description" v-else>
            {{ $t('INSTALLED_TO_DEVICE', {
              installedAt: new Date(entry.queued_at * 1000).toLocaleString(),
              deviceModel: entry.device_model
            }) }}
          </p>
        </div>
      </div>
      <div v-if="entry.error" class="error">
        {{ entry.error?.translated }}
      </div>
    </div>

    <div class="actions" @click.stop>
      <v-button ref="installButton" type="primary" :loading="isInstalling" @click.native="install">
        {{ $t('REINSTALL') }}
      </v-button>

      <template v-if="!getDownloadFailureReason(entry)">
        <v-button v-if="isInternalDownload(entry)" :loading="isStartingInternalDownload"
          @click.native="startInternalDownload(entry)">
          {{ $t('DOWNLOAD') }}
        </v-button>
        <v-button v-else tag="router-link" :to="getExternalDownloadLink(entry)" :target="'_blank'">
          {{ $t('DOWNLOAD') }}
        </v-button>
      </template>

      <!-- Enable JIT Button -->
      <v-button v-if="entry.supports_jit === 1" :loading="isEnablingJit" @click.native="confirmEnableJit">
        {{ $t('ENABLE_JIT_BUTTON') }}
      </v-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import AppIcon from '@/components/AppIcon.vue';
import VButton from '@/components/Button.vue';
import installApp from '@/mixins/installApp';
import downloadApp from '@/mixins/downloadApp';

export default {
  mixins: [installApp, downloadApp],
  components: {
    AppIcon,
    VButton,
  },
  props: {
    entry: Object,
  },
  data() {
    return {
      isInstalling: false,
      isEnablingJit: false,
    };
  },
  computed: {
    ...mapGetters(['config']),
    downloadLink() {
      const ticket = this.entry.download_ticket;
      if (!ticket) {
        return '';
      }

      return `/redirect/${ticket}`;
    },
    detailsLink() {
      return `/details/${this.entry.object.universal_object_identifier}`;
    },
  },
  methods: {
    navigateToDetails() {
      this.$router.push(this.detailsLink);
    },
    async confirmEnableJit() {
      // Check if user can install apps
      if (!this.$store.getters.canInstallApps) {
        await this.$alert(this.$t('TO_ENABLE_JIT_VISIT_FROM_DEVICE', { device: this.config.nice_idevice_model }));
        return;
      }

      try {
        // Ask if the app is installed
        const confirmed = await this.$confirm(this.$t('CONFIRM_APP_INSTALLED'), {
          okText: this.$t('YES'),
          cancelText: this.$t('NO'),
        });

        if (confirmed) {
          this.startEnableJitSession();
        }
      } catch {
        // User clicked No or canceled
      }
    },
    async startEnableJitSession() {
      // Set loading state
      this.isEnablingJit = true;

      try {
        // Make API call to start JIT session
        const response = await this.$api.call('start_enable_jit_session', {
          installation_uuid: this.entry.uuid,
        });

        console.log('JIT session API response:', response);

        if (response && response.bundle_id) {
          // Show JIT enable modal after ensuring Vue is ready
          this.$nextTick(() => {
            this.$modal.show('jit-enable', {
              bundleId: response.bundle_id,
            });
          });
        } else {
          // Handle error case
          this.$notify({
            type: 'error',
            text: this.$t('FAILED_START_JIT_SESSION'),
          });
        }
      } catch (error) {
        console.error('Error starting JIT session:', error);
        this.$notify({
          type: 'error',
          text: this.$t('FAILED_START_JIT_SESSION'),
        });
      } finally {
        this.isEnablingJit = false;
      }
    },
    async install() {
      if (this.entry.no_installation_ticket_failure_reason) {
        this.$notify({
          type: 'error',
          text: this.entry.no_installation_ticket_failure_reason.translated,
        });
      }

      this.isInstalling = true;
      try {
        await this.installApp(this.entry.object.id, 'universal', { installationTicket: this.entry.installation_ticket, showSuccessConfirm: false });
        if (this.config.link_type === 'mdm') {
          this.$refs.installButton.showTooltip({ content: this.$t('INSTALLATION_QUEUED') });
        }
      } finally {
        this.isInstalling = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.entry {
  display: flex;
  gap: 15px;
  padding: 10px;
  border: 1px solid var(--tertiary-accent);
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;

  &:hover {
    background-color: var(--secondary-bg);
  }

  &:focus {
    outline: 2px solid var(--primary-accent);
    outline-offset: -2px;
  }
}

.heading {
  font-size: 16px;
}

.jit-badge {
  background-color: var(--lighter-primary);
  color: var(--primary);
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 2px;
  margin-left: 3px;
  position: relative;
  top: -0.5em;
  vertical-align: baseline;
  display: inline-block;
}

.description {
  line-height: 1.2;
  margin-top: 6px;
}

.info {
  &__top {
    display: flex;
    gap: 15px;
  }
}

.error {
  margin-top: 10px;
  color: var(--text-error);
}

.details,
.actions {
  display: flex;
  flex-direction: column;
}

.actions {
  margin-left: auto;
  flex-shrink: 0;
  gap: 3px;
  z-index: 1;
  /* Ensure buttons are clickable */
}
</style>
